import { ArrowRight, Palette, Award, Leaf, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useSiteContent, getContentValue } from "@/hooks/useSiteContent";
import villageMarket from "@/assets/village-market.webp";
import villageRoad from "@/assets/village-road.webp";

const StorySection = () => {
  const { data: sectionContent } = useSiteContent("story_section_content");
  
  const content = sectionContent?.value as Record<string, string> | undefined;
  const badge = getContentValue(content, "badge", "Our Story");
  const title = getContentValue(content, "title", "Bridging Villages to");
  const titleHighlight = getContentValue(content, "title_highlight", "Your Doorstep");
  const paragraph1 = getContentValue(content, "paragraph1", "Deep in rural India, skilled artisans practice their craft for generations. From Madhubani paintings to terracotta pottery, each piece carries centuries of tradition.");
  const paragraph2 = getContentValue(content, "paragraph2", "We partner directly with village communities, ensuring fair wages while bringing you authentic, handcrafted treasures.");
  const ctaText = getContentValue(content, "cta_text", "Read Our Full Story");
  
  const stat1Value = getContentValue(content, "stat1_value", "100%");
  const stat1Label = getContentValue(content, "stat1_label", "Handcrafted");
  const stat2Value = getContentValue(content, "stat2_value", "Fair");
  const stat2Label = getContentValue(content, "stat2_label", "Trade Certified");
  const stat3Value = getContentValue(content, "stat3_value", "Eco");
  const stat3Label = getContentValue(content, "stat3_label", "Friendly");

  return (
    <section className="py-14 md:py-20 bg-secondary overflow-hidden relative">
      {/* Warli-style Decorative Patterns */}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none">
        {/* Women carrying pots - Warli style */}
        <svg className="absolute top-8 right-8 w-36 h-28 text-earth" viewBox="0 0 140 100" fill="none">
          {/* Figure 1 */}
          <circle cx="30" cy="12" r="6" fill="currentColor" />
          <ellipse cx="30" cy="5" rx="8" ry="4" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <line x1="30" y1="18" x2="30" y2="45" stroke="currentColor" strokeWidth="2" />
          <line x1="30" y1="25" x2="20" y2="35" stroke="currentColor" strokeWidth="2" />
          <line x1="30" y1="25" x2="40" y2="32" stroke="currentColor" strokeWidth="2" />
          <line x1="30" y1="45" x2="22" y2="65" stroke="currentColor" strokeWidth="2" />
          <line x1="30" y1="45" x2="38" y2="65" stroke="currentColor" strokeWidth="2" />
          {/* Figure 2 */}
          <circle cx="70" cy="15" r="6" fill="currentColor" />
          <ellipse cx="70" cy="8" rx="8" ry="4" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <line x1="70" y1="21" x2="70" y2="48" stroke="currentColor" strokeWidth="2" />
          <line x1="70" y1="28" x2="60" y2="38" stroke="currentColor" strokeWidth="2" />
          <line x1="70" y1="28" x2="80" y2="35" stroke="currentColor" strokeWidth="2" />
          <line x1="70" y1="48" x2="62" y2="68" stroke="currentColor" strokeWidth="2" />
          <line x1="70" y1="48" x2="78" y2="68" stroke="currentColor" strokeWidth="2" />
          {/* Figure 3 */}
          <circle cx="110" cy="10" r="6" fill="currentColor" />
          <ellipse cx="110" cy="3" rx="8" ry="4" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <line x1="110" y1="16" x2="110" y2="43" stroke="currentColor" strokeWidth="2" />
          <line x1="110" y1="23" x2="100" y2="33" stroke="currentColor" strokeWidth="2" />
          <line x1="110" y1="23" x2="120" y2="30" stroke="currentColor" strokeWidth="2" />
          <line x1="110" y1="43" x2="102" y2="63" stroke="currentColor" strokeWidth="2" />
          <line x1="110" y1="43" x2="118" y2="63" stroke="currentColor" strokeWidth="2" />
        </svg>
        
        {/* Peacock - Warli style */}
        <svg className="absolute bottom-16 left-6 w-28 h-32 text-earth" viewBox="0 0 100 120" fill="none">
          <circle cx="50" cy="25" r="8" fill="currentColor" />
          <path d="M50 33 Q50 50 50 70" stroke="currentColor" strokeWidth="2" />
          <line x1="50" y1="70" x2="42" y2="95" stroke="currentColor" strokeWidth="2" />
          <line x1="50" y1="70" x2="58" y2="95" stroke="currentColor" strokeWidth="2" />
          {/* Tail feathers */}
          <path d="M50 45 Q20 20 15 50" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path d="M50 45 Q25 30 25 60" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path d="M50 45 Q35 35 40 65" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path d="M50 45 Q80 20 85 50" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path d="M50 45 Q75 30 75 60" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path d="M50 45 Q65 35 60 65" stroke="currentColor" strokeWidth="1.5" fill="none" />
          {/* Feather eyes */}
          <circle cx="18" cy="48" r="3" stroke="currentColor" strokeWidth="1" fill="none" />
          <circle cx="28" cy="58" r="3" stroke="currentColor" strokeWidth="1" fill="none" />
          <circle cx="82" cy="48" r="3" stroke="currentColor" strokeWidth="1" fill="none" />
          <circle cx="72" cy="58" r="3" stroke="currentColor" strokeWidth="1" fill="none" />
        </svg>
        
        {/* Farming scene - person with tool */}
        <svg className="absolute top-1/3 left-1/4 w-20 h-28 text-earth" viewBox="0 0 80 110" fill="none">
          <circle cx="40" cy="15" r="7" fill="currentColor" />
          <line x1="40" y1="22" x2="40" y2="55" stroke="currentColor" strokeWidth="2" />
          <line x1="40" y1="30" x2="25" y2="45" stroke="currentColor" strokeWidth="2" />
          <line x1="40" y1="30" x2="60" y2="25" stroke="currentColor" strokeWidth="2" />
          <line x1="60" y1="25" x2="70" y2="15" stroke="currentColor" strokeWidth="1.5" />
          <line x1="40" y1="55" x2="30" y2="80" stroke="currentColor" strokeWidth="2" />
          <line x1="40" y1="55" x2="50" y2="80" stroke="currentColor" strokeWidth="2" />
        </svg>
        
        {/* Spiral sun */}
        <svg className="absolute bottom-8 right-1/4 w-20 h-20 text-earth" viewBox="0 0 100 100" fill="none">
          <circle cx="50" cy="50" r="12" fill="currentColor" />
          <path d="M50 50 Q55 35 50 20" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path d="M50 50 Q65 45 80 50" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path d="M50 50 Q55 65 50 80" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path d="M50 50 Q35 55 20 50" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path d="M50 50 Q60 35 70 25" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path d="M50 50 Q65 60 75 75" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path d="M50 50 Q40 65 30 75" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path d="M50 50 Q35 40 25 25" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
        
        {/* Fish pattern */}
        <svg className="absolute top-12 left-1/2 w-28 h-16 text-earth" viewBox="0 0 110 60" fill="none">
          <path d="M10 30 Q25 20 40 30 Q25 40 10 30" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <circle cx="35" cy="30" r="2" fill="currentColor" />
          <path d="M10 30 L2 22 M10 30 L2 38" stroke="currentColor" strokeWidth="1.5" />
          
          <path d="M55 25 Q70 15 85 25 Q70 35 55 25" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <circle cx="80" cy="25" r="2" fill="currentColor" />
          <path d="M55 25 L47 17 M55 25 L47 33" stroke="currentColor" strokeWidth="1.5" />
        </svg>
        
        {/* Geometric border bottom */}
        <svg className="absolute bottom-0 left-0 w-full h-6 text-earth" viewBox="0 0 400 24" preserveAspectRatio="none" fill="none">
          {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330, 360, 390].map((x) => (
            <path key={x} d={`M${x} 24 L${x + 15} 12 L${x + 30} 24`} stroke="currentColor" strokeWidth="1" fill="none" />
          ))}
        </svg>
      </div>
      
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Images */}
          <div className="relative">
            <div className="relative z-10">
              <img
                src={villageMarket}
                alt="Village artisans at traditional market"
                className="rounded-2xl shadow-elevated w-full max-w-lg"
                loading="lazy"
                decoding="async"
                width={512}
                height={341}
              />
            </div>
            <div className="absolute -bottom-8 -right-4 md:-right-12 w-2/3 z-20">
              <img
                src={villageRoad}
                alt="Scenic village road surrounded by trees"
                className="rounded-2xl shadow-elevated border-4 border-background"
                loading="lazy"
                decoding="async"
                width={405}
                height={270}
              />
            </div>
            {/* Decorative Element */}
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-terracotta/20 rounded-full blur-2xl" />
            <div className="absolute -bottom-6 right-1/4 w-32 h-32 bg-olive/20 rounded-full blur-2xl" />
          </div>

          {/* Content */}
          <div className="lg:pl-4">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-olive/10 text-olive rounded-full text-sm font-semibold mb-4">
              <BookOpen className="h-4 w-4" />
              {badge}
            </span>
            
            <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4 leading-tight">
              {title}{" "}
              <span className="text-terracotta">{titleHighlight}</span>
            </h2>
            
            <p className="text-muted-foreground leading-relaxed mb-4">
              {paragraph1}
            </p>
            
            <p className="text-muted-foreground leading-relaxed mb-6">
              {paragraph2}
            </p>

            <div className="flex flex-wrap gap-6 mb-6">
              <div className="flex items-center gap-3 bg-background/50 rounded-xl px-4 py-3">
                <div className="w-10 h-10 bg-terracotta/10 rounded-full flex items-center justify-center">
                  <Palette className="h-5 w-5 text-terracotta" />
                </div>
                <div>
                  <p className="font-display text-xl font-bold text-primary">{stat1Value}</p>
                  <p className="text-muted-foreground text-xs">{stat1Label}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-background/50 rounded-xl px-4 py-3">
                <div className="w-10 h-10 bg-olive/10 rounded-full flex items-center justify-center">
                  <Award className="h-5 w-5 text-olive" />
                </div>
                <div>
                  <p className="font-display text-xl font-bold text-primary">{stat2Value}</p>
                  <p className="text-muted-foreground text-xs">{stat2Label}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 bg-background/50 rounded-xl px-4 py-3">
                <div className="w-10 h-10 bg-golden/10 rounded-full flex items-center justify-center">
                  <Leaf className="h-5 w-5 text-golden" />
                </div>
                <div>
                  <p className="font-display text-xl font-bold text-primary">{stat3Value}</p>
                  <p className="text-muted-foreground text-xs">{stat3Label}</p>
                </div>
              </div>
            </div>

            <Button variant="accent" size="lg" className="group" asChild>
              <Link to="/story">
                {ctaText}
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StorySection;
